import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";
import { Text } from "../Text";

const tooltipVariants = cva(
  "fixed z-50 bg-popup-fill-intense text-action-ink-on-primary-normal rounded-medium border border-popup-outline-subtle flex flex-col  p-4 rounded-xlarge min-w-[200px] max-w-[300px] transition-opacity duration-200",
  {
    variants: {
      isVisible: {
        true: "opacity-100 pointer-events-auto shadow-[0_4px_20px_rgba(0,0,0,0.15)]",
        false: "opacity-0 pointer-events-none",
      },
    },
    defaultVariants: {
      isVisible: false,
    },
  }
);

const tooltipArrowVariants = cva(
  "absolute w-0 h-0 border-solid border-[6px] -translate-x-1/2",
  {
    variants: {
      placement: {
        "top-start":
          "top-full border-t-popup-fill-intense border-x-transparent border-b-transparent",
        top: "top-full border-t-popup-fill-intense border-x-transparent border-b-transparent",
        "top-end":
          "top-full border-t-popup-fill-intense border-x-transparent border-b-transparent",
        "bottom-start":
          "bottom-full border-b-popup-fill-intense border-x-transparent border-t-transparent",
        bottom:
          "bottom-full border-b-popup-fill-intense border-x-transparent border-t-transparent",
        "bottom-end":
          "bottom-full border-b-popup-fill-intense border-x-transparent border-t-transparent",
      },
    },
    defaultVariants: {
      placement: "top",
    },
  }
);

export interface TooltipProps {
  /**
   * The trigger element that the tooltip will attach to
   */
  children: React.ReactElement;
  /**
   * Heading text for the tooltip
   */
  heading?: string;
  /**
   * Description text for the tooltip
   */
  description: string;
  /**
   * Placement of the tooltip relative to the trigger
   */
  placement?:
    | "top-start"
    | "top"
    | "top-end"
    | "bottom-start"
    | "bottom"
    | "bottom-end";
  /**
   * Whether to show the arrow/anchor
   */
  showArrow?: boolean;
  /**
   * Custom class name for the tooltip container
   */
  className?: string;
  /**
   * Delay before showing tooltip (in milliseconds)
   */
  delay?: number;
  /**
   * Whether the tooltip is disabled
   */
  disabled?: boolean;
}

const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>(
  (
    {
      children,
      heading,
      description,
      placement = "top",
      showArrow = true,
      className,
      delay = 200,
      disabled = false,
    },
    ref
  ) => {
    const [isVisible, setIsVisible] = React.useState(false);
    const [position, setPosition] = React.useState({ top: 0, left: 0 });
    const [arrowPosition, setArrowPosition] = React.useState({ left: 0 });
    const [actualPlacement, setActualPlacement] = React.useState<
      "top-start" | "top" | "top-end" | "bottom-start" | "bottom" | "bottom-end"
    >(placement);
    const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const triggerRef = React.useRef<HTMLElement>(null);
    const tooltipRef = React.useRef<HTMLDivElement>(null);

    const calculatePosition = React.useCallback(() => {
      if (!triggerRef.current || !tooltipRef.current) return;

      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const gap = 8; // 8px gap between trigger and tooltip
      const arrowSize = 6; // Size of the arrow
      const viewportPadding = 8; // Minimum padding from viewport edges

      let top = 0;
      let left = 0;
      let currentPlacement = placement;

      // Calculate initial position based on placement
      switch (placement) {
        case "top-start":
          top = triggerRect.top - tooltipRect.height - gap - arrowSize;
          left = triggerRect.left;
          break;
        case "top":
          top = triggerRect.top - tooltipRect.height - gap - arrowSize;
          left =
            triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
          break;
        case "top-end":
          top = triggerRect.top - tooltipRect.height - gap - arrowSize;
          left = triggerRect.right - tooltipRect.width;
          break;
        case "bottom-start":
          top = triggerRect.bottom + gap + arrowSize;
          left = triggerRect.left;
          break;
        case "bottom":
          top = triggerRect.bottom + gap + arrowSize;
          left =
            triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
          break;
        case "bottom-end":
          top = triggerRect.bottom + gap + arrowSize;
          left = triggerRect.right - tooltipRect.width;
          break;
      }

      // Get viewport dimensions
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Adjust horizontal position to keep tooltip within viewport
      if (left < viewportPadding) {
        // Tooltip would overflow on the left
        left = viewportPadding;
      } else if (left + tooltipRect.width > viewportWidth - viewportPadding) {
        // Tooltip would overflow on the right
        left = viewportWidth - tooltipRect.width - viewportPadding;
      }

      // Adjust vertical position to keep tooltip within viewport
      if (top < viewportPadding) {
        // Tooltip would overflow at the top
        // Try to flip to bottom if there's more space there
        const spaceBelow = viewportHeight - triggerRect.bottom;
        const spaceAbove = triggerRect.top;

        if (spaceBelow > spaceAbove) {
          // Flip to bottom
          top = triggerRect.bottom + gap + arrowSize;
          // Update placement to reflect the flip
          if (placement === "top-start") currentPlacement = "bottom-start";
          else if (placement === "top") currentPlacement = "bottom";
          else if (placement === "top-end") currentPlacement = "bottom-end";
        } else {
          // Keep at top but adjust to stay in viewport
          top = viewportPadding;
        }
      } else if (top + tooltipRect.height > viewportHeight - viewportPadding) {
        // Tooltip would overflow at the bottom
        // Try to flip to top if there's more space there
        const spaceAbove = triggerRect.top;
        const spaceBelow = viewportHeight - triggerRect.bottom;

        if (spaceAbove > spaceBelow) {
          // Flip to top
          top = triggerRect.top - tooltipRect.height - gap - arrowSize;
          // Update placement to reflect the flip
          if (placement === "bottom-start") currentPlacement = "top-start";
          else if (placement === "bottom") currentPlacement = "top";
          else if (placement === "bottom-end") currentPlacement = "top-end";
        } else {
          // Keep at bottom but adjust to stay in viewport
          top = viewportHeight - tooltipRect.height - viewportPadding;
        }
      }

      // Calculate arrow position relative to trigger
      // The arrow should point to the center of the trigger element
      const triggerCenterX = triggerRect.left + triggerRect.width / 2;
      const tooltipLeft = left;
      const arrowLeft = triggerCenterX - tooltipLeft;

      // Clamp arrow position to stay within tooltip bounds (with padding)
      const arrowPadding = 16; // Minimum distance from tooltip edges
      const clampedArrowLeft = Math.max(
        arrowPadding,
        Math.min(arrowLeft, tooltipRect.width - arrowPadding)
      );

      setPosition({ top, left });
      setArrowPosition({ left: clampedArrowLeft });
      setActualPlacement(currentPlacement);
    }, [placement]);

    const handleMouseEnter = () => {
      if (disabled) return;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setIsVisible(true);
      }, delay);
    };

    const handleMouseLeave = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setIsVisible(false);
    };

    const handleFocus = () => {
      if (disabled) return;
      setIsVisible(true);
    };

    const handleBlur = () => {
      setIsVisible(false);
    };

    React.useEffect(() => {
      if (isVisible) {
        calculatePosition();
        window.addEventListener("scroll", calculatePosition, true);
        window.addEventListener("resize", calculatePosition);
      }

      return () => {
        window.removeEventListener("scroll", calculatePosition, true);
        window.removeEventListener("resize", calculatePosition);
      };
    }, [isVisible, calculatePosition]);

    React.useEffect(() => {
      // Reset actualPlacement when placement prop changes
      setActualPlacement(placement);
    }, [placement]);

    React.useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    // Merge refs function
    const mergeRefs = (...refs: any[]) => {
      return (node: any) => {
        refs.forEach((ref) => {
          if (typeof ref === "function") {
            ref(node);
          } else if (ref && typeof ref === "object" && "current" in ref) {
            (ref as any).current = node;
          }
        });
      };
    };

    // Clone the child element and add event handlers
    const trigger = React.cloneElement(children, {
      ref: mergeRefs(triggerRef, (children as any).ref),
      onMouseEnter: (e: React.MouseEvent) => {
        handleMouseEnter();
        children.props.onMouseEnter?.(e);
      },
      onMouseLeave: (e: React.MouseEvent) => {
        handleMouseLeave();
        children.props.onMouseLeave?.(e);
      },
      onFocus: (e: React.FocusEvent) => {
        handleFocus();
        children.props.onFocus?.(e);
      },
      onBlur: (e: React.FocusEvent) => {
        handleBlur();
        children.props.onBlur?.(e);
      },
      "aria-describedby": isVisible ? "tooltip-content" : undefined,
    });

    return (
      <>
        {trigger}

        <div
          ref={mergeRefs(tooltipRef, ref)}
          id="tooltip-content"
          role="tooltip"
          className={cn(tooltipVariants({ isVisible }), className)}
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
          }}
          aria-hidden={!isVisible}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {showArrow && (
            <div
              className={cn(
                tooltipArrowVariants({ placement: actualPlacement })
              )}
              style={{
                left: `${arrowPosition.left}px`,
              }}
            />
          )}

          <div className="relative flex flex-col gap-2">
            {heading && (
              <Text
                variant="body"
                size="medium"
                weight="semibold"
                color="onPrimary"
              >
                {heading}
              </Text>
            )}
            <Text
              variant="body"
              size="small"
              weight="regular"
              color="onPrimary"
              //   className="flex gap-3"
            >
              {description}
            </Text>
          </div>
        </div>
      </>
    );
  }
);

Tooltip.displayName = "Tooltip";

export { Tooltip, tooltipVariants };
